import React, { useRef, useEffect, useState, useCallback } from 'react'
import isEmpty from 'lodash/isEmpty'
import { createUseStyles } from 'react-jss'
import theme from '../../../style/theme'
import { sliceMarginStyles, span } from '../../../style/span'
import { vw } from '../../../style/vw'
import last from 'lodash/last'
import map from 'lodash/map'
import gsap from 'gsap'
import toArray from 'lodash/toArray'
import sum from 'lodash/sum'
import useWindowWidthResize from '../../../hooks/useWindowWidthResize'

export default function LogoTicker ({ label, items }) {
  const classes = useStyles()
  const itemsRef = useRef(null)
  const [itemsList, setItemsList] = useState(items)
  const localsRef = useRef({})

  const resize = useCallback(() => {
    if (localsRef.current.tl) {
      localsRef.current.tl.kill()
      localsRef.current.tl = null
    }

    if (!itemsRef.current) return
    const itemElements = toArray(itemsRef.current.children).slice(0, items.length)
    const containerWidth = itemsRef.current.getBoundingClientRect().width
    const itemsWidth = sum(map(itemElements, e => e.getBoundingClientRect().width))

    const count = Math.ceil((containerWidth + itemsWidth) / itemsWidth)
    var list = []
    for (let i = 0; i < count; i++) {
      list = [...list, ...items]
    }
    setItemsList(list)
    const tl = gsap.timeline({ repeat: -1 })
    tl.fromTo(itemsRef.current, { x: 0 }, { x: `-${itemsWidth}px`, duration: itemsWidth / 50, ease: 'linear' }) // We move 50 pixes per second
    localsRef.current.tl = tl
  }, [items])

  useEffect(() => {
    resize()
  }, [items, resize])

  useWindowWidthResize(resize)

  if (isEmpty(items)) {
    return null
  }

  return (
    <div className={classes.container}>
      {label && <span className={classes.label}>{label}</span>}
      <div className={classes.itemsContainer}>
        <div className={classes.items} ref={itemsRef}>
          {itemsList.map((item, i) => (
            <div key={i} className={classes.item}>
              <img
                src={last(item.logo.sizes).url}
                alt={item.logo.alt || ''}
                className={classes.logoImage}
                style={{
                  aspectRatio: `${item.logo.aspect}`,
                  ...(item.customHeight && {
                    '--custom-height': `${item.customHeight}px`
                  })
                }}
              />
            </div>
          ))}
        </div>
        <div className={classes.fadeLeft} />
        <div className={classes.fadeRight} />
      </div>
    </div>
  )
}

const useStyles = createUseStyles({
  container: {
    extend: [sliceMarginStyles],
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    paddingLeft: 0,
    borderBottom: [1, 'solid', theme.colors.border],
    [theme.breakpoints.up('md')]: {
      paddingLeft: span(1, 'md'),
      flexDirection: 'row',
      minHeight: vw(82, 'desktop')
    }
  },
  label: {
    fontSize: vw(16),
    color: theme.colors.primary,
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    whiteSpace: 'nowrap',
    flexGrow: 0,
    flexShrink: 0,
    marginTop: vw(16),
    marginRight: 0,
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      fontSize: vw(16, 'desktop'),
      marginRight: vw(32, 'desktop')
    }
  },
  itemsContainer: {
    overflow: 'hidden',
    width: '100%',
    position: 'relative'
  },
  items: {
    overflow: 'visible',
    display: 'flex',
    whiteSpace: 'nowrap',
    marginBottom: vw(16),
    marginTop: vw(16),
    [theme.breakpoints.up('md')]: {
      marginBottom: vw(24, 'desktop'),
      marginTop: vw(24, 'desktop')
    }
  },
  item: {
    flexGrow: 0,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    '& img': {
      marginRight: vw(24),
      [theme.breakpoints.up('md')]: {
        marginRight: vw(72, 'desktop')
      }
    }
  },
  logoImage: {
    width: 'auto',
    height: `var(--custom-height, ${vw(20)})`,
    maxHeight: vw(20),
    [theme.breakpoints.up('md')]: {
      height: `var(--custom-height, ${vw(32, 'desktop')})`,
      maxHeight: vw(32, 'desktop')
    }
  },
  fadeLeft: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: vw(40),
    height: '100%',
    background: `linear-gradient(to right, ${theme.colors.background} 0%, transparent 100%)`,
    pointerEvents: 'none',
    zIndex: 1,
    [theme.breakpoints.up('md')]: {
      width: vw(80, 'desktop')
    }
  },
  fadeRight: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: vw(40),
    height: '100%',
    background: `linear-gradient(to left, ${theme.colors.background} 0%, transparent 100%)`,
    pointerEvents: 'none',
    zIndex: 1,
    [theme.breakpoints.up('md')]: {
      width: vw(80, 'desktop')
    }
  }
})
